import React, {useCallback, useEffect, useState} from 'react'
import {useRouter} from 'next/router'

import BankCardLineIcon from 'remixicon-react/BankCardLineIcon'
import BillLineIcon from 'remixicon-react/BillLineIcon'
import styled from 'styled-components'
import {media} from 'styled-bootstrap-grid'

import {theme} from 'src/themes'
import {SpacingWrapper} from 'src/components/layout'
import {Button} from 'src/components/buttons'
import {
  CheckoutRadioCard,
  CreditCardForm,
  CreditCardInfo,
  CreditCardRadioCard,
  Valitor3DSecureProcessingForm,
} from 'src/components/checkout'

import {useCheckout} from 'src/context/CheckoutContext'
import {useUser} from 'src/context/UserContext'
import {removeSpaces} from 'src/utils/strings'
import type {TokenCreateRequest, VirtualCard} from 'src/api/rest'
import {restApi} from 'src/api/rest'
import {paths} from 'src/utils/paths'
import {toast} from 'react-toastify'

import {checkoutErrorCodeMap} from 'src/constants'
import type {
  CreditCardFormErrorProps,
  CreditCardFormValues,
} from './CreditCardFormFields'
import CreditCardFormFields, {
  validateCreditCardFormFields,
} from './CreditCardFormFields'
import {DeleteConfirmation, Modal} from '../common'

export enum PaymentType {
  Valitor,
  Reikningur,
}

export const paymenIdMap = {
  [PaymentType.Valitor]: 'reon.payments.valitor_pay',
  [PaymentType.Reikningur]: 'mirumee.payments.dummy',
}

export const paymentTypeMap: Record<string, PaymentType> = {
  'reon.payments.valitor_pay': PaymentType.Valitor,
  'mirumee.payments.dummy': PaymentType.Reikningur,
}

interface PaymentProps {
  setPaymentInProgress: React.Dispatch<React.SetStateAction<boolean>>
}

const IndentWrapper = styled.div`
  ${media.md`
      padding-left: 56px;
    `}
`

export default function Payment({
  setPaymentInProgress,
}: PaymentProps): JSX.Element {
  const router = useRouter()
  const {valitorCards, removeValitorCard, currentCompany} = useUser()
  const {completeCheckout, createPayment, clearCart, checkout} = useCheckout()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [cardIdToRemove, setCardIdToRemove] = useState<string>()

  const [cardIsVerified, setCardIsVerified] = useState<boolean>(false)
  const [valitorActionUrl, setValitorActionUrl] = useState<string>()
  const [valitorVerificationFields, setValitorVerificationFields] = useState()
  const [selectedPaymentId, setSelectedPaymentId] = useState<string>()
  const [selectedCardId, setSelectedCardId] = useState<string>()
  const [newCardSelected, setNewCardSelected] = useState<boolean>(false)

  const [cardFormValues, setCardFormValues] = useState<CreditCardFormValues>({
    cardNumber: '',
    expDate: '',
    cvc: '',
    store: false,
  })
  const [cardErrors, setCardErrors] = useState<CreditCardFormErrorProps>({
    cardNumber: '',
    expDate: '',
    cvc: '',
  })

  function onError(msg?: string): void {
    toast.error(msg || 'Greiðsla mistókst')
    setIsLoading(false)
  }

  const selectedCard = valitorCards
    ? selectedCardId && valitorCards.find(({id}) => id === selectedCardId)
    : undefined

  const paymentGateways = checkout?.availablePaymentGateways?.filter(
    (pg) =>
      pg.id === paymenIdMap[PaymentType.Valitor] ||
      (currentCompany &&
        currentCompany.invoicingAllowed &&
        pg.id === paymenIdMap[PaymentType.Reikningur]),
  )

  useEffect(() => {
    if (paymentGateways?.length === 1) {
      setSelectedPaymentId(paymentGateways[0].id)
    }
  }, [paymentGateways])

  const handleSelectVirtualCard = useCallback(
    (id: string) => {
      setSelectedCardId(id)
      setNewCardSelected(false)
    },
    [setSelectedCardId, setNewCardSelected],
  )

  const handleSelectNewCard = useCallback(() => {
    setSelectedCardId('')
    setNewCardSelected(true)
  }, [setSelectedCardId, setNewCardSelected])

  const handleDeleteConfirmation = useCallback(
    (card: VirtualCard) => {
      setCardIdToRemove(card.id)
      setShowDeleteModal(true)
    },
    [setCardIdToRemove, setShowDeleteModal],
  )

  const handleDeleteCreditCard = useCallback(() => {
    setSelectedCardId(undefined)
    if (cardIdToRemove) {
      removeValitorCard(cardIdToRemove)
    }
    setShowDeleteModal(false)
  }, [removeValitorCard, setSelectedCardId, setShowDeleteModal, cardIdToRemove])

  const createValitorCardToken = useCallback(
    (data: TokenCreateRequest) =>
      restApi
        .paymentsValitorPayTokensCreate(data)
        .then((res) => res?.data?.token)
        .catch(() => undefined),
    [],
  )

  const onChangeCard = useCallback(
    (e: React.BaseSyntheticEvent) => {
      const {target} = e
      const value = target.type === 'checkbox' ? target.checked : target.value
      const {name} = target
      setCardFormValues({
        ...cardFormValues,
        [name]: value,
      })
    },

    [cardFormValues],
  )

  const onSubmit = useCallback(async () => {
    setPaymentInProgress(true)
    setIsLoading(true)

    let paymentToken
    if (selectedPaymentId === paymenIdMap[PaymentType.Valitor]) {
      if (newCardSelected || !valitorCards?.length) {
        const formErrors = validateCreditCardFormFields({
          cardNumber: cardFormValues?.cardNumber,
          expDate: cardFormValues?.expDate,
          cvc: cardFormValues?.cvc,
        })
        if (Object.keys(formErrors)?.length) {
          setCardErrors(formErrors)
          setIsLoading(false)
          return
        }
        const {cardNumber, expDate, cvc, store} = cardFormValues
        const date = expDate.split('/')
        const year = `20${date[1]}`
        paymentToken = await createValitorCardToken({
          cardNumber: removeSpaces(cardNumber),
          expMonth: parseInt(date[0], 10),
          expYear: parseInt(year, 10),
          cvc,
          store,
        })

        if (!paymentToken) {
          onError('Athugaðu hvort kortaupplýsingar séu réttar')
          return
        }
      } else {
        if (!selectedCard || typeof selectedCard === 'string') {
          return
        }
        const cardId = selectedCard.id
        if (!cardId) {
          return
        }
        paymentToken = await createValitorCardToken({
          virtualCardId: cardId,
        })
      }
    } else if (selectedPaymentId === paymenIdMap[PaymentType.Reikningur]) {
      paymentToken = 'InvoiceToken'
    }

    const payment = selectedPaymentId
      ? await createPayment({
          gateway: selectedPaymentId,
          token: paymentToken,
        })
      : null

    const error = payment?.errors?.[0]
    if (error) {
      onError(checkoutErrorCodeMap[error?.code])
      return
    }
    // Here we start the actual pay process, we have to poll whether the user has accepted payment via third party
    const checkoutCompleteData = await completeCheckout()

    const checkoutError = checkoutCompleteData?.errors?.[0]
    if (checkoutError && !checkoutCompleteData?.confirmationNeeded) {
      if (checkoutError?.code === 'INSUFFICIENT_STOCK') {
        const productName = checkoutError?.message?.split(':')[1]
        onError(`Ekki nægilegt magn til á lager af${productName}`)
      } else {
        onError(checkoutErrorCodeMap[checkoutError.code])
      }
      return
    }
    const orderToken = checkoutCompleteData?.order?.token
    if (
      checkoutCompleteData?.confirmationNeeded &&
      selectedPaymentId === paymenIdMap[PaymentType.Valitor]
    ) {
      const data = JSON.parse(checkoutCompleteData?.confirmationData)
      setCardIsVerified(data?.isSuccess)
      setValitorActionUrl(data?.postUrl)
      setValitorVerificationFields(data?.verificationFields)
    }
    if (orderToken) {
      router.push(`${paths.order}${orderToken}/`).then(() => {
        clearCart()
      })
    } else {
      setIsLoading(false)
      setPaymentInProgress(false)
    }
  }, [
    setPaymentInProgress,
    selectedPaymentId,
    createPayment,
    completeCheckout,
    newCardSelected,
    valitorCards?.length,
    cardFormValues,
    createValitorCardToken,
    selectedCard,
    router,
    clearCart,
  ])

  return (
    <>
      {paymentGateways?.map((pg) => (
        <CheckoutRadioCard
          key={pg.id}
          title={
            pg.id === paymenIdMap[PaymentType.Valitor]
              ? 'Borga með greiðslukorti'
              : 'Setja í reikning'
          }
          icon={
            pg.id === paymenIdMap[PaymentType.Valitor] ? (
              <BankCardLineIcon size={30} color={theme.palette.primary} />
            ) : (
              <BillLineIcon size={30} color={theme.palette.primary} />
            )
          }
          selected={selectedPaymentId === pg.id}
          onSelect={() => setSelectedPaymentId(pg.id)}
        >
          {pg.id === paymenIdMap[PaymentType.Valitor] && (
            <SpacingWrapper spacing={12}>
              {valitorCards?.length ? (
                <>
                  {valitorCards?.map((card) => (
                    <CreditCardRadioCard
                      key={card.id}
                      selected={card === selectedCard}
                      onSelect={() => handleSelectVirtualCard(card.id)}
                    >
                      <CreditCardInfo
                        cardInfo={card}
                        selected={card === selectedCard}
                        onSelect={() => handleSelectVirtualCard(card.id)}
                        onDelete={() => handleDeleteConfirmation(card)}
                      />
                    </CreditCardRadioCard>
                  ))}
                  <CreditCardRadioCard
                    selected={newCardSelected}
                    onSelect={handleSelectNewCard}
                  >
                    <CreditCardForm
                      selected={newCardSelected}
                      onSelect={handleSelectNewCard}
                      errors={cardErrors}
                      values={cardFormValues}
                      onChange={onChangeCard}
                    />
                  </CreditCardRadioCard>
                </>
              ) : (
                <CreditCardFormFields
                  errors={cardErrors}
                  values={cardFormValues}
                  onChange={onChangeCard}
                />
              )}
            </SpacingWrapper>
          )}
        </CheckoutRadioCard>
      ))}

      <SpacingWrapper spacing={36}>
        <IndentWrapper>
          <Button
            withGutter
            color="secondary"
            outlined
            loading={isLoading}
            disabled={
              !selectedPaymentId ||
              !checkout?.billingAddress ||
              (selectedPaymentId === paymenIdMap[PaymentType.Valitor] &&
                !!valitorCards?.length &&
                !selectedCard &&
                !newCardSelected)
            }
            onClick={onSubmit}
          >
            Klára kaup
          </Button>
        </IndentWrapper>
      </SpacingWrapper>
      {valitorActionUrl && cardIsVerified && valitorVerificationFields ? (
        <Valitor3DSecureProcessingForm
          actionUrl={valitorActionUrl}
          isSuccess={cardIsVerified}
          fields={valitorVerificationFields}
        />
      ) : null}
      <Modal setShow={setShowDeleteModal} show={showDeleteModal}>
        <DeleteConfirmation
          title="Ertu viss um að þú viljir eyða kortaupplýsingunum þínum?"
          text="Þú munt ekki geta tekið þessa aðgerð til baka!"
          onCancel={() => setShowDeleteModal(false)}
          onRemove={handleDeleteCreditCard}
          cancelText="Hætta við"
          submitText="Eyða korti"
        />
      </Modal>
    </>
  )
}
